import { apiClient, unwrap } from '@/lib/apiClient';
import type { DocumentKind } from '@/features/institute/admissions/constants';
import type { CollegeStudentEditPayload } from '@/features/institute/admissions/schemas/collegeAdmission.schema';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type { ExportFieldOption, ExportRows } from '@/types/export';
import type {
  RollNumberParams,
  RollNumberPlan,
  RollSummary,
  Student,
  StudentDocument,
  StudentListItem,
  StudentListParams,
  StudentStatus,
  TransportRouteOption,
} from '@/types/student';

/**
 * Every call the roster makes.
 *
 * As with the rest of the institute area there is no institute id in any path — the
 * server takes it from the caller's token, so there is nothing for a client to
 * point at another institute's children. The academic year is the server's
 * decision too: the roster is always this session's.
 */

/** The attendance column's month, as `YYYY-MM`, and the school's working days in it. */
export interface MonthWorkingDays {
  month: string;
  workingDays: number;
}

/** The session the register is for. Its dates, `YYYY-MM-DD`, bound the attendance months. */
export interface AcademicYearSpan {
  uuid: string;
  name: string;
  startDate: string;
  endDate: string;
  /** False when the register is showing a past session. */
  isCurrent: boolean;
}

/** One session the year filter offers. */
export interface AcademicYearOption {
  uuid: string;
  name: string;
  isCurrent: boolean;
}

/** A page of the register, plus the envelope the table's pagination needs. */
export interface StudentListResult {
  items: StudentListItem[];
  /**
   * How the register breaks down by standing, counted over the *same* filters
   * as the page — so the summary always describes what is on screen rather
   * than quoting a whole-year total beside three filtered rows.
   */
  byStatus: Record<StudentStatus, number>;
  /**
   * The five figures above the roll. Unlike `byStatus` these describe the whole
   * session, so they do not move as the reader narrows the list.
   */
  summary: RollSummary;
  /**
   * Children this session missing a paper their admission needs. Whole-session,
   * like `summary`, so it does not shrink as the list is narrowed.
   */
  documentsPending: number;
  /**
   * The month the attendance column counts, and the days the school was open in
   * it — weekly offs and declared holidays left out, cut to the session and to
   * today. Null when nothing of that month falls inside the session.
   */
  attendanceMonth: MonthWorkingDays | null;
  academicYear: AcademicYearSpan;
  /** Every session the institute has had, newest first — the year filter's options. */
  academicYears: AcademicYearOption[];
  meta: PaginationMeta;
}

/** What every documents call answers with: the papers on file, and what is still needed. */
export interface StudentDocumentsState {
  documents: StudentDocument[];
  missingDocuments: DocumentKind[];
}

/** What the edit screen sends back. The fees and admission number are not its to change. */
export type StudentUpdatePayload = Record<string, unknown>;

export const studentsApi = {
  list: async (params: StudentListParams): Promise<StudentListResult> => {
    // Blank filters are dropped so the URL stays readable and the server does
    // not have to treat "" as "all".
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
    );

    const response = await apiClient.get<
      ApiSuccess<{
        items: StudentListItem[];
        byStatus: Record<StudentStatus, number>;
        summary: RollSummary;
        documentsPending: number;
        attendanceMonth: MonthWorkingDays | null;
        academicYear: AcademicYearSpan;
        academicYears: AcademicYearOption[];
      }>
    >('/institute/students', { params: query });

    const { items, byStatus, summary, documentsPending, attendanceMonth, academicYear, academicYears } =
      response.data.data;

    return {
      items,
      byStatus,
      summary,
      documentsPending,
      attendanceMonth,
      academicYear,
      academicYears,
      meta: response.data.meta ?? { page: 1, limit: 20, total: items.length, totalPages: 1 },
    };
  },

  /** What numbering a section would do, without doing it. */
  previewRollNumbers: (params: RollNumberParams) =>
    unwrap(
      apiClient.get<ApiSuccess<RollNumberPlan>>('/institute/students/roll-numbers/preview', { params }),
    ),

  /** Numbers the section. The server recomputes the plan, so it saves what the data says now. */
  assignRollNumbers: (params: RollNumberParams) =>
    unwrap(apiClient.post<ApiSuccess<RollNumberPlan>>('/institute/students/roll-numbers', params)),

  /** Routes and buses already on this institute's students — the transport box's suggestions. */
  transportOptions: () =>
    unwrap(apiClient.get<ApiSuccess<TransportRouteOption[]>>('/institute/students/transport-options')),

  get: (uuid: string) => unwrap(apiClient.get<ApiSuccess<Student>>(`/institute/students/${uuid}`)),

  update: (uuid: string, payload: StudentUpdatePayload) =>
    unwrap(apiClient.patch<ApiSuccess<Student>>(`/institute/students/${uuid}`, payload)),

  /** Private, like every upload — needs the access token, so use SecureImage. */
  photoPath: (uuid: string) => `/institute/students/${uuid}/photo`,

  /**
   * Replaces a child's photograph.
   *
   * Its own call rather than a field on `update`, because it carries bytes: the
   * patch is a JSON correction the server validates with zod, and mixing a file
   * into it would turn every save on the edit screen into a multipart request.
   */
  uploadPhoto: (uuid: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    // Content-Type is left unset on purpose: the browser must add the
    // multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<Student>>(`/institute/students/${uuid}/photo`, form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  /** Private, like the photograph — fetch it with the access token (`downloadPrivateFile`). */
  documentPath: (uuid: string, field: DocumentKind) => `/institute/students/${uuid}/documents/${field}`,

  /**
   * Attaches a certificate to a child already on the roll, replacing the one in
   * that slot. How papers reach a record that came in from a spreadsheet.
   */
  uploadDocument: (uuid: string, field: DocumentKind, file: File) => {
    const form = new FormData();
    form.append('file', file);
    // Unset so the browser adds the multipart boundary itself.
    return unwrap(
      apiClient.put<ApiSuccess<StudentDocumentsState>>(
        `/institute/students/${uuid}/documents/${field}`,
        form,
        { headers: { 'Content-Type': undefined } },
      ),
    );
  },

  /** Takes a certificate off the record. The slot reads as pending again if it is required. */
  removeDocument: (uuid: string, field: DocumentKind) =>
    unwrap(
      apiClient.delete<ApiSuccess<StudentDocumentsState>>(
        `/institute/students/${uuid}/documents/${field}`,
      ),
    ),

  /**
   * Correcting a college student's record.
   *
   * Its own call rather than a flag on `update`, because the body is a
   * different shape: a college has no class, no section, no guardians and no
   * fees, and the server rejects the school body at a college and vice versa.
   */
  updateForCollege: (uuid: string, payload: CollegeStudentEditPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<{ uuid: string; admissionNumber: string; fullName: string }>>(
        `/institute/students/${uuid}/college`,
        payload,
      ),
    ),

  /**
   * Sends the student a fresh link to set their password, opening the account
   * first if their admission could not. Any earlier link stops working.
   */
  resendInvite: (uuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<{ account: 'INVITED' | 'NO_EMAIL' | 'EMAIL_TAKEN' }>>(
        `/institute/students/${uuid}/invite`,
      ),
    ),

  /**
   * Sends the student's parent a fresh link to the *parent* panel, opening that
   * account first if nobody has asked for one yet.
   *
   * A separate account from the student's, so a separate call: the office picks
   * which of the two a family needs, and for a young child it is this one.
   */
  resendGuardianInvite: (uuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<{ account: 'INVITED'; sentTo: string }>>(
        `/institute/students/${uuid}/guardian-invite`,
      ),
    ),

  /**
   * Suspends a student: off every register, record kept, reinstatable. Not a
   * delete — the student stays on this screen with a "Suspended" badge.
   */
  suspend: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<Student>>(`/institute/students/${uuid}/suspend`)),

  reinstate: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<Student>>(`/institute/students/${uuid}/reinstate`)),

  /** Every detail the download dialog can include, grouped. */
  exportFields: () =>
    unwrap(apiClient.get<ApiSuccess<ExportFieldOption[]>>('/institute/students/export/fields')),

  /**
   * The filtered register with the chosen details — every row, not one page.
   * Audited on the server, which is why it is its own call rather than the
   * list walked page by page.
   */
  exportRows: (filters: StudentExportFilters, fields: string[]) =>
    unwrap(
      apiClient.get<ApiSuccess<ExportRows & { academicYear: string }>>('/institute/students/export', {
        params: {
          ...Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined),
          ),
          fields: fields.join(','),
        },
      }),
    ),
};

/** What a download can be narrowed by: the register's filters, without paging. */
export type StudentExportFilters = Omit<StudentListParams, 'page' | 'limit' | 'attendanceMonth'>;
